import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileText, Globe } from 'lucide-react';
import Toast from '../Toast';
import {
  SettingsCard,
  SettingsSwitch,
  SettingsTextField,
} from './settingsUi.jsx';
import { useToast } from '../../hooks/useToast';
import apiService from '../../services/api';
import {
  footerLinkBadge,
  pageStatusBadge,
} from '../../utils/sitePublicStatusUi.js';

const PANELS = [
  { id: 'footer', label: 'Pied de page & contacts' },
  { id: 'acquisition', label: 'Acquisition & Prospection' },
  { id: 'pages', label: 'Pages' },
];

const STATUS_LABEL = {
  UNPUBLISHED: 'NON PUBLIÉ',
  DRAFT_CHANGED: 'BROUILLON MODIFIÉ',
  PUBLISHED: 'PUBLIÉ',
};

const KIND_LABEL = {
  INSTITUTIONAL: 'Institutionnel',
  LEGAL: 'Légal',
};

const PAGE_ORDER = [
  'about',
  'contact',
  'professionnels',
  'mentions-legales',
  'cgv',
  'cgu',
  'confidentialite',
  'cookies',
];

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('fr-FR');
  } catch {
    return '—';
  }
}

export default function SitePublicSettings() {
  const { toasts, toast, removeToast } = useToast();
  const [panel, setPanel] = useState('footer');
  const [loading, setLoading] = useState(true);
  const [chrome, setChrome] = useState(null);
  const [pages, setPages] = useState([]);
  const [editingSlug, setEditingSlug] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [chromeRes, pagesRes] = await Promise.all([
        apiService.getSiteChrome(),
        apiService.getSitePages(),
      ]);
      setChrome(chromeRes);
      setPages(pagesRes?.pages || []);
    } catch (error) {
      toast.error(error?.message || 'Impossible de charger le site public');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const orderedPages = useMemo(() => {
    const order = new Map(PAGE_ORDER.map((slug, i) => [slug, i]));
    return [...pages].sort((a, b) => (order.get(a.slug) ?? 99) - (order.get(b.slug) ?? 99));
  }, [pages]);

  const handleError = useCallback((message) => {
    toast.error(message);
  }, []);

  const saveChrome = async (patch) => {
    setSaving(true);
    try {
      const updated = await apiService.updateSiteChrome(patch);
      setChrome(updated);
      toast.success('Pied de page enregistré');
    } catch (error) {
      toast.error(error?.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !chrome) {
    return (
      <SettingsCard icon={Globe} title="Site public" subtitle="Chargement…">
        {toasts.map((item) => (
          <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
        ))}
        <p className="text-sm text-gray-500">Chargement des contenus publics.</p>
      </SettingsCard>
    );
  }

  return (
    <div className="space-y-4" data-testid="site-public-settings">
      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
      <div className="flex flex-wrap gap-2">
        {PANELS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setPanel(item.id)}
            className={`rounded-xl px-3 py-1.5 text-sm font-medium ${
              panel === item.id
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {panel === 'footer' ? (
        <FooterPanel
          chrome={chrome}
          setChrome={setChrome}
          pages={orderedPages}
          saving={saving}
          onSave={saveChrome}
          onToggleEnabled={async (slug, enabled) => {
            const updated = await apiService.patchSitePageEnabled(slug, enabled);
            setPages((prev) => prev.map((p) => (p.slug === slug ? { ...p, ...updated } : p)));
          }}
        />
      ) : null}

      {panel === 'acquisition' ? (
        <AcquisitionPanel
          chrome={chrome}
          setChrome={setChrome}
          saving={saving}
          onSave={saveChrome}
        />
      ) : null}

      {panel === 'pages' ? (
        <PagesPanel
          pages={orderedPages}
          editingSlug={editingSlug}
          onEdit={setEditingSlug}
          onPublished={async () => {
            await loadAll();
            toast.success('Page publiée');
          }}
          onDraftSaved={() => toast.success('Brouillon enregistré')}
          onError={handleError}
        />
      ) : null}
    </div>
  );
}

function FooterPanel({ chrome, setChrome, pages, saving, onSave, onToggleEnabled }) {
  const pageBySlug = new Map(pages.map((p) => [p.slug, p]));
  return (
    <SettingsCard
      icon={Globe}
      title="Pied de page & contacts"
      subtitle="Textes, e-mails publics et liens système. Les routes restent fixes."
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-gray-600">Description du pied de page</span>
            <textarea
              value={chrome.footerDescription || ''}
              onChange={(e) => setChrome((prev) => ({ ...prev, footerDescription: e.target.value }))}
              rows={3}
              className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
            />
          </label>
        </div>
        <SettingsTextField
          label="Contact public"
          type="email"
          value={chrome.publicContactEmail || ''}
          onChange={(publicContactEmail) => setChrome((prev) => ({ ...prev, publicContactEmail }))}
        />
        <SettingsTextField
          label="Support client"
          type="email"
          value={chrome.supportEmail || ''}
          onChange={(supportEmail) => setChrome((prev) => ({ ...prev, supportEmail }))}
        />
      </div>

      <div className="mt-6">
        <p className="mb-3 text-sm font-semibold text-gray-800">Liens système</p>
        <div className="space-y-2">
          {(chrome.footerLinks || []).map((link, index) => {
            const page = pageBySlug.get(link.slug);
            const badge = footerLinkBadge({
              published: Boolean(page?.publishedRevisionId),
              enabled: page?.enabled,
            });
            return (
              <div
                key={link.slug}
                className="grid grid-cols-1 items-center gap-3 rounded-xl border border-gray-100 px-3 py-2 sm:grid-cols-[auto_1fr_auto_auto_auto]"
              >
                <SettingsSwitch
                  checked={page?.enabled !== false}
                  label={`Afficher ${link.label}`}
                  onChange={(enabled) => onToggleEnabled(link.slug, enabled)}
                />
                <input
                  value={link.label}
                  onChange={(e) => {
                    const footerLinks = chrome.footerLinks.map((item, i) =>
                      i === index ? { ...item, label: e.target.value } : item
                    );
                    setChrome((prev) => ({ ...prev, footerLinks }));
                  }}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
                />
                <span className="text-xs text-gray-400">/{link.slug}</span>
                <span className={badge.className}>{badge.label}</span>
                <input
                  type="number"
                  value={link.order}
                  onChange={(e) => {
                    const footerLinks = chrome.footerLinks.map((item, i) =>
                      i === index ? { ...item, order: Number(e.target.value) } : item
                    );
                    setChrome((prev) => ({ ...prev, footerLinks }));
                  }}
                  className="w-20 rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
                />
              </div>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Un lien n’apparaît dans le footer que s’il est activé et que la page est publiée.
          La gestion commerciale des leads est dans Gestion NR2J → Prospection.
          Le bloc « Vous êtes professionnel ? » se règle dans Acquisition &amp; Prospection.
        </p>
      </div>

      <div className="mt-5">
        <button
          type="button"
          disabled={saving}
          onClick={() => onSave({
            footerDescription: chrome.footerDescription,
            publicContactEmail: chrome.publicContactEmail,
            supportEmail: chrome.supportEmail,
            footerLinks: chrome.footerLinks,
          })}
          className="inline-flex items-center rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </SettingsCard>
  );
}

function AcquisitionPanel({ chrome, setChrome, saving, onSave }) {
  return (
    <SettingsCard
      icon={Globe}
      title="Acquisition & Prospection"
      subtitle="Réglages techniques du site public — hors pipeline commercial quotidien."
    >
      <div className="space-y-4" data-testid="acquisition-prospection-settings">
        <div className="flex flex-col gap-2 rounded-xl border border-gray-100 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900">
              Afficher le lien « Vous êtes professionnel ? » dans le pied de page
            </p>
            <p className="mt-0.5 text-xs text-gray-500">
              Affiche ou masque l’accès à la page professionnels dans le pied de page public Clickorico.
            </p>
          </div>
          <SettingsSwitch
            checked={chrome.proBlockEnabled !== false}
            label="Afficher le lien professionnels"
            onChange={(proBlockEnabled) => setChrome((prev) => ({ ...prev, proBlockEnabled }))}
          />
        </div>
        <SettingsTextField
          label="Email de contact professionnel"
          type="email"
          value={chrome.proContactEmail || ''}
          onChange={(proContactEmail) => setChrome((prev) => ({ ...prev, proContactEmail }))}
        />
        <p className="-mt-2 text-xs text-gray-500">
          Adresse affichée / utilisée pour les contacts professionnels publics.
        </p>
        <button
          type="button"
          disabled={saving}
          onClick={() => onSave({
            proBlockEnabled: chrome.proBlockEnabled,
            proContactEmail: chrome.proContactEmail,
          })}
          className="inline-flex items-center rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </SettingsCard>
  );
}

function PagesPanel({ pages, editingSlug, onEdit, onPublished, onDraftSaved, onError }) {
  if (editingSlug) {
    return (
      <PageEditor
        slug={editingSlug}
        onBack={() => onEdit(null)}
        onPublished={onPublished}
        onDraftSaved={onDraftSaved}
        onError={onError}
      />
    );
  }

  return (
    <SettingsCard icon={FileText} title="Pages" subtitle="Brouillon, aperçu, publication. Aucun texte légal n’est publié automatiquement.">
      <div className="space-y-2">
        {pages.map((page) => {
          const badge = pageStatusBadge(page);
          return (
          <div
            key={page.slug}
            className="flex flex-col gap-2 rounded-xl border border-gray-100 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div>
              <p className="font-medium text-gray-900">{page.draftTitle || page.title}</p>
              <p className="text-xs text-gray-500">
                {KIND_LABEL[page.kind] || page.kind}
                {page.publishedAt ? ` · ${formatDate(page.publishedAt)}` : ''}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={badge.className}>{badge.label}</span>
              <button
                type="button"
                onClick={() => onEdit(page.slug)}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
              >
                Éditer
              </button>
            </div>
          </div>
          );
        })}
      </div>
    </SettingsCard>
  );
}

function PageEditor({ slug, onBack, onPublished, onDraftSaved, onError }) {
  const [page, setPage] = useState(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftMarkdown, setDraftMarkdown] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [busy, setBusy] = useState(false);
  const [viewingRevision, setViewingRevision] = useState(null);
  const [requiresReacceptance, setRequiresReacceptance] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiService.getSitePage(slug).then((data) => {
      if (cancelled) return;
      setPage(data);
      setDraftTitle(data.draftTitle || '');
      setDraftMarkdown(data.draftMarkdown || '');
      setRequiresReacceptance(false);
    }).catch((error) => onError(error?.message || 'Chargement impossible'));
    return () => {
      cancelled = true;
    };
  }, [slug, onError]);

  if (!page) {
    return <SettingsCard icon={FileText} title="Édition" subtitle="Chargement…" />;
  }

  const showReacceptanceOption = Boolean(page.acceptanceRelevant);

  return (
    <SettingsCard
      icon={FileText}
      title={page.draftTitle || page.title}
      subtitle={`${KIND_LABEL[page.kind]} · ${STATUS_LABEL[page.status]}`}
    >
      <button type="button" onClick={onBack} className="mb-4 text-sm text-primary-700">
        ← Retour à la liste
      </button>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <SettingsTextField label="Titre du brouillon" value={draftTitle} onChange={setDraftTitle} />
          <label className="mt-4 block">
            <span className="mb-1.5 block text-[13px] font-medium text-gray-600">Markdown</span>
            <textarea
              value={draftMarkdown}
              onChange={(e) => setDraftMarkdown(e.target.value)}
              rows={16}
              className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 font-mono text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
            />
          </label>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const updated = await apiService.saveSitePageDraft(slug, { draftTitle, draftMarkdown });
                  setPage(updated);
                  onDraftSaved();
                } catch (error) {
                  onError(error?.message || 'Brouillon non enregistré');
                } finally {
                  setBusy(false);
                }
              }}
              className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
            >
              Enregistrer le brouillon
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const preview = await apiService.previewSitePage(slug, draftMarkdown);
                  setPreviewHtml(preview.sanitizedHtml || '');
                } catch (error) {
                  onError(error?.message || 'Aperçu impossible');
                } finally {
                  setBusy(false);
                }
              }}
              className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
            >
              Aperçu
            </button>
          </div>
          {showReacceptanceOption ? (
            <label className="mt-3 flex items-start gap-2 rounded-xl border border-amber-100 bg-amber-50 px-3 py-2 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={requiresReacceptance}
                onChange={(e) => setRequiresReacceptance(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600"
              />
              <span>
                Cette nouvelle version nécessite une nouvelle acceptation des utilisateurs
                <span className="mt-0.5 block text-xs text-gray-500">
                  Décoché = typo / mise en forme (aucune popup). Coché = changement juridique (réacceptation une fois).
                </span>
              </span>
            </label>
          ) : null}
          <div className="mt-3">
            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await apiService.saveSitePageDraft(slug, { draftTitle, draftMarkdown });
                  const updated = await apiService.publishSitePage(slug, {
                    requiresReacceptance: showReacceptanceOption ? requiresReacceptance : false,
                  });
                  setPage(updated);
                  setRequiresReacceptance(false);
                  onPublished();
                } catch (error) {
                  onError(error?.message || 'Publication impossible');
                } finally {
                  setBusy(false);
                }
              }}
              className="rounded-xl bg-primary-600 px-3 py-2 text-sm text-white"
            >
              Publier
            </button>
          </div>
        </div>
        <div>
          <p className="mb-1.5 text-[13px] font-medium text-gray-600">Aperçu</p>
          <div
            className="prose prose-sm min-h-[220px] max-w-none rounded-xl border border-gray-100 bg-gray-50 p-4"
            dangerouslySetInnerHTML={{ __html: previewHtml || '<p class="text-gray-400">Aucun aperçu.</p>' }}
          />
          <p className="mt-6 mb-2 text-sm font-semibold text-gray-800">Historique</p>
          <div className="space-y-2">
            {(page.revisions || []).map((rev) => (
              <div key={rev.id} className="rounded-lg border border-gray-100 px-3 py-2 text-sm">
                <p className="font-medium">{rev.title}</p>
                <p className="text-xs text-gray-500">
                  {formatDate(rev.publishedAt)}
                  {rev.publishedBy ? ` · ${rev.publishedBy.prenom} ${rev.publishedBy.nom}` : ''}
                  {rev.requiresReacceptance ? ' · réacceptation requise' : ''}
                </p>
                <div className="mt-1 flex gap-3">
                  <button type="button" className="text-primary-700" onClick={() => setViewingRevision(rev)}>
                    Voir
                  </button>
                  <button
                    type="button"
                    className="text-gray-700"
                    onClick={async () => {
                      try {
                        const updated = await apiService.restoreSitePageRevision(slug, rev.id);
                        setPage(updated);
                        setDraftTitle(updated.draftTitle);
                        setDraftMarkdown(updated.draftMarkdown);
                        onDraftSaved();
                      } catch (error) {
                        onError(error?.message || 'Reprise impossible');
                      }
                    }}
                  >
                    Reprendre comme brouillon
                  </button>
                </div>
              </div>
            ))}
            {(page.revisions || []).length === 0 ? (
              <p className="text-sm text-gray-500">Aucune révision publiée.</p>
            ) : null}
          </div>
          {viewingRevision ? (
            <p className="mt-3 text-xs text-gray-500">
              Révision du {formatDate(viewingRevision.publishedAt)} — titre « {viewingRevision.title} ».
              Reprendre comme brouillon pour la modifier, puis publier.
            </p>
          ) : null}
        </div>
      </div>
    </SettingsCard>
  );
}
